import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CalendarIcon, CheckCircleIcon, MapPinIcon, InfoIcon } from 'lucide-react';
import { VaccineAppointmentModal } from '../components/VaccineAppointmentModal';
export function Immunizations() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState('');
  const openModal = (vaccine: string) => {
    setSelectedVaccine(vaccine);
    setModalOpen(true);
  };
  const vaccineCatalog = [{
    name: 'COVID-19 Booster',
    desc: 'Updated seasonal booster for protection against current variants.',
    age: '6 months+',
    price: 'Covered by most insurance'
  }, {
    name: 'Flu (Seasonal)',
    desc: 'Annual quadrivalent vaccine to protect against seasonal influenza.',
    age: '3 years+',
    price: '$25 or covered by insurance'
  }, {
    name: 'Tdap',
    desc: 'Protection against Tetanus, Diphtheria, and Pertussis (Whooping Cough).',
    age: '11 years+',
    price: 'Covered by most insurance'
  }, {
    name: 'Pneumonia',
    desc: 'Prevents pneumococcal disease and related complications.',
    age: '65+ or high risk',
    price: 'Covered by most insurance'
  }, {
    name: 'Shingles',
    desc: 'Two-dose series to prevent shingles and post-herpetic neuralgia.',
    age: '50 years+',
    price: 'Covered by most insurance'
  }, {
    name: 'Travel Vaccinations',
    desc: 'Hepatitis A/B, Typhoid, and other region-specific protections.',
    age: 'Varies',
    price: 'Varies by vaccine'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="bg-white min-h-screen pb-20" data-id="element-442">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 mb-12 md:mb-16" data-id="element-443">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-clinical-blue/5 text-clinical-blue rounded-full text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-444">
          Clinical Services
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 uppercase tracking-tighter" data-id="element-445">
          Immunization Services
        </h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed" data-id="element-446">
          Protect yourself and your loved ones. Our certified pharmacists
          provide a wide range of vaccinations in a safe, clinical environment.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-447">
        <div className="grid lg:grid-cols-3 gap-12 items-start" data-id="element-448">
          <div className="lg:col-span-2 space-y-8" data-id="element-449">
            <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm" data-id="element-450">
              <div className="p-6 sm:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center" data-id="element-451">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3" data-id="element-452">
                  <ShieldCheckIcon className="w-6 h-6 text-clinical-blue" data-id="element-453" />
                  Available Vaccines
                </h2>
                <div className="hidden sm:flex items-center gap-2 text-clinical-blue font-bold text-[10px] uppercase tracking-widest" data-id="element-454">
                  <span className="w-2 h-2 bg-clinical-blue rounded-full animate-pulse" data-id="element-455"></span>
                  In Stock
                </div>
              </div>
              <div className="divide-y divide-slate-50" data-id="element-456">
                {vaccineCatalog.map((v, i) => <div key={i} className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:bg-slate-50/50 transition-colors" data-id="element-457">
                    <div className="max-w-md" data-id="element-458">
                      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-459">
                        {v.name}
                      </h3>
                      <p className="text-slate-500 text-sm mb-3 leading-relaxed" data-id="element-460">
                        {v.desc}
                      </p>
                      <div className="flex flex-wrap gap-4" data-id="element-461">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-462">
                          <InfoIcon className="w-3 h-3" data-id="element-463" /> Age: {v.age}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-clinical-blue uppercase tracking-widest" data-id="element-464">
                          <CheckCircleIcon className="w-3 h-3" data-id="element-465" /> {v.price}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => openModal(v.name)} className="bg-clinical-blue text-white font-black py-3 px-6 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] whitespace-nowrap shadow-md shadow-clinical-blue/10" data-id="element-466">
                      Schedule Appointment
                    </button>
                  </div>)}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-467">
              <div className="bg-clean-grey p-8 rounded-2xl border border-slate-100" data-id="element-468">
                <MapPinIcon className="w-8 h-8 text-clinical-blue mb-4" data-id="element-469" />
                <h3 className="text-lg font-bold text-slate-900 mb-2 uppercase tracking-tight" data-id="element-470">
                  Annapolis Pharmacy
                </h3>
                <p className="text-slate-500 text-sm mb-4" data-id="element-471">
                  2002 Jennifer Rd, Annapolis, MD 21401
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-widest" data-id="element-472">
                  Walk-ins Welcome
                </div>
              </div>
              <div className="bg-clean-grey p-8 rounded-2xl border border-slate-100" data-id="element-473">
                <MapPinIcon className="w-8 h-8 text-clinical-blue mb-4" data-id="element-474" />
                <h3 className="text-lg font-bold text-slate-900 mb-2 uppercase tracking-tight" data-id="element-475">
                  Washington D.C.
                </h3>
                <p className="text-slate-500 text-sm mb-4" data-id="element-476">
                  1234 Pennsylvania Ave NW, DC 20004
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-widest" data-id="element-477">
                  Walk-ins Welcome
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-478">
            <div className="bg-clinical-blue p-8 md:p-10 rounded-3xl text-white" data-id="element-479">
              <CalendarIcon className="w-12 h-12 mb-6 opacity-50" data-id="element-480" />
              <h2 className="text-2xl font-black mb-4 uppercase tracking-tight" data-id="element-481">
                Quick Scheduling
              </h2>
              <p className="text-blue-100 mb-8 text-sm leading-relaxed" data-id="element-482">
                Most vaccinations take less than 15 minutes. Schedule now to
                skip the wait.
              </p>
              <button onClick={() => openModal('General Vaccination')} className="w-full bg-white text-clinical-blue font-black py-4 rounded-xl hover:bg-blue-50 transition-colors uppercase tracking-widest text-[10px] shadow-xl" data-id="element-483">
                Book General Visit
              </button>
            </div>

            <div className="p-8 bg-slate-900 rounded-3xl text-white" data-id="element-484">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-tight" data-id="element-485">
                What to Bring
              </h3>
              <ul className="space-y-3" data-id="element-486">
                {['Insurance Card', 'Photo ID', 'Vaccination Record'].map((item, i) => <li key={i} className="flex items-center gap-3 text-sm text-slate-400" data-id="element-487">
                      <CheckCircleIcon className="w-4 h-4 text-clinical-blue" data-id="element-488" />
                      {item}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <VaccineAppointmentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} vaccineName={selectedVaccine} data-id="element-489" />
    </motion.div>;
}